/**
 * Marketplace Redux Slice - State management for function discovery and commerce
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { marketplaceService, FunctionListing, FunctionCategory, SearchResult, SearchParams } from '../../services/marketplace';

interface MarketplaceState {
  // Search and Discovery
  searchResults: SearchResult | null;
  searchLoading: boolean;
  searchError: string | null;
  currentSearchParams: SearchParams;

  // Categories
  categories: FunctionCategory[];
  categoriesLoading: boolean;

  // Featured and Popular Functions
  featuredFunctions: FunctionListing[];
  popularFunctions: FunctionListing[];
  recentFunctions: FunctionListing[];

  // Function Details
  selectedFunction: FunctionListing | null;
  functionLoading: boolean;

  // User's Functions
  purchasedFunctions: FunctionListing[];
  wishlistFunctions: FunctionListing[];
  wishlistIds: Set<string>;

  // UI State
  viewMode: 'grid' | 'list';
  filters: {
    category?: string;
    pricing?: 'free' | 'paid' | 'subscription' | 'usage';
    minRating?: number;
    tags?: string[];
  };
}

const initialState: MarketplaceState = {
  searchResults: null,
  searchLoading: false,
  searchError: null,
  currentSearchParams: { sortBy: 'relevance' },

  categories: [],
  categoriesLoading: false,

  featuredFunctions: [],
  popularFunctions: [],
  recentFunctions: [],

  selectedFunction: null,
  functionLoading: false,

  purchasedFunctions: [],
  wishlistFunctions: [],
  wishlistIds: new Set(),

  viewMode: 'grid',
  filters: {}
};

// Async Thunks
export const searchFunctions = createAsyncThunk(
  'marketplace/searchFunctions',
  async (params: SearchParams, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.searchFunctions(params);
      if (response.status === 'success' && response.data) {
        return { result: response.data, params };
      }
      throw new Error('Search failed');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Search failed');
    }
  }
);

export const loadCategories = createAsyncThunk(
  'marketplace/loadCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.getCategories();
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load categories');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load categories');
    }
  }
);

export const loadFeaturedFunctions = createAsyncThunk(
  'marketplace/loadFeaturedFunctions',
  async (limit: number = 20, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.getFeaturedFunctions(limit);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load featured functions');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load featured functions');
    }
  }
);

export const loadPopularFunctions = createAsyncThunk(
  'marketplace/loadPopularFunctions',
  async (limit: number = 20, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.getPopularFunctions(limit);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load popular functions');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load popular functions');
    }
  }
);

export const loadRecentFunctions = createAsyncThunk(
  'marketplace/loadRecentFunctions',
  async (limit: number = 20, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.getRecentFunctions(limit);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load recent functions');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load recent functions');
    }
  }
);

export const loadFunctionDetails = createAsyncThunk(
  'marketplace/loadFunctionDetails',
  async (functionId: string, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.getFunctionDetails(functionId);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load function details');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load function details');
    }
  }
);

export const loadWishlist = createAsyncThunk(
  'marketplace/loadWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.getWishlist(1, 100);
      if (response.status === 'success' && response.data) {
        return response.data.functions;
      }
      throw new Error('Failed to load wishlist');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load wishlist');
    }
  }
);

export const addToWishlist = createAsyncThunk(
  'marketplace/addToWishlist',
  async (functionId: string, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.addToWishlist(functionId);
      if (response.status === 'success') {
        return functionId;
      }
      throw new Error('Failed to add to wishlist');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to add to wishlist');
    }
  }
);

export const removeFromWishlist = createAsyncThunk(
  'marketplace/removeFromWishlist',
  async (functionId: string, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.removeFromWishlist(functionId);
      if (response.status === 'success') {
        return functionId;
      }
      throw new Error('Failed to remove from wishlist');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to remove from wishlist');
    }
  }
);

export const purchaseFunction = createAsyncThunk(
  'marketplace/purchaseFunction',
  async (request: { functionId: string; pricingTier?: string }, { rejectWithValue }) => {
    try {
      const response = await marketplaceService.purchaseFunction(request);
      if (response.status === 'success' && response.data) {
        return { functionId: request.functionId, purchase: response.data };
      }
      throw new Error('Purchase failed');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Purchase failed');
    }
  }
);

// Slice
const marketplaceSlice = createSlice({
  name: 'marketplace',
  initialState,
  reducers: {
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },

    setFilters: (state, action: PayloadAction<MarketplaceState['filters']>) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    clearFilters: (state) => {
      state.filters = {};
    },

    clearSearchResults: (state) => {
      state.searchResults = null;
      state.searchError = null;
      state.currentSearchParams = { sortBy: 'relevance' };
    },

    clearSelectedFunction: (state) => {
      state.selectedFunction = null;
    },

    updateFunctionRating: (state, action: PayloadAction<{ functionId: string; rating: number; reviewCount: number }>) => {
      const { functionId, rating, reviewCount } = action.payload;

      // Update in search results
      if (state.searchResults) {
        const funcIndex = state.searchResults.functions.findIndex(f => f.id === functionId);
        if (funcIndex !== -1) {
          state.searchResults.functions[funcIndex].stats.rating = rating;
          state.searchResults.functions[funcIndex].stats.reviewCount = reviewCount;
        }
      }

      // Update in featured functions
      const featuredIndex = state.featuredFunctions.findIndex(f => f.id === functionId);
      if (featuredIndex !== -1) {
        state.featuredFunctions[featuredIndex].stats.rating = rating;
        state.featuredFunctions[featuredIndex].stats.reviewCount = reviewCount;
      }

      // Update in popular functions
      const popularIndex = state.popularFunctions.findIndex(f => f.id === functionId);
      if (popularIndex !== -1) {
        state.popularFunctions[popularIndex].stats.rating = rating;
        state.popularFunctions[popularIndex].stats.reviewCount = reviewCount;
      }

      // Update selected function
      if (state.selectedFunction && state.selectedFunction.id === functionId) {
        state.selectedFunction.stats.rating = rating;
        state.selectedFunction.stats.reviewCount = reviewCount;
      }
    }
  },
  extraReducers: (builder) => {
    // Search Functions
    builder
      .addCase(searchFunctions.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchFunctions.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.result;
        state.currentSearchParams = action.payload.params;
      })
      .addCase(searchFunctions.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload as string;
      });

    // Load Categories
    builder
      .addCase(loadCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(loadCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(loadCategories.rejected, (state) => {
        state.categoriesLoading = false;
      });

    // Load Featured Functions
    builder
      .addCase(loadFeaturedFunctions.fulfilled, (state, action) => {
        state.featuredFunctions = action.payload;
      });

    // Load Popular Functions
    builder
      .addCase(loadPopularFunctions.fulfilled, (state, action) => {
        state.popularFunctions = action.payload;
      });

    // Load Recent Functions
    builder
      .addCase(loadRecentFunctions.fulfilled, (state, action) => {
        state.recentFunctions = action.payload;
      });

    // Load Function Details
    builder
      .addCase(loadFunctionDetails.pending, (state) => {
        state.functionLoading = true;
      })
      .addCase(loadFunctionDetails.fulfilled, (state, action) => {
        state.functionLoading = false;
        state.selectedFunction = action.payload;
      })
      .addCase(loadFunctionDetails.rejected, (state) => {
        state.functionLoading = false;
      });

    // Load Wishlist
    builder
      .addCase(loadWishlist.fulfilled, (state, action) => {
        state.wishlistFunctions = action.payload;
        state.wishlistIds = new Set(action.payload.map(f => f.id));
      });

    // Add to Wishlist
    builder
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.wishlistIds.add(action.payload);
      });

    // Remove from Wishlist
    builder
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.wishlistIds.delete(action.payload);
        state.wishlistFunctions = state.wishlistFunctions.filter(f => f.id !== action.payload);
      });

    // Purchase Function
    builder
      .addCase(purchaseFunction.fulfilled, (state, action) => {
        // Add to purchased functions if we have the function data
        const functionId = action.payload.functionId;

        // Find the function in our current data
        let purchasedFunction: FunctionListing | undefined;

        if (state.selectedFunction && state.selectedFunction.id === functionId) {
          purchasedFunction = state.selectedFunction;
        } else if (state.searchResults) {
          purchasedFunction = state.searchResults.functions.find(f => f.id === functionId);
        }

        if (!purchasedFunction) {
          purchasedFunction = state.featuredFunctions.find(f => f.id === functionId) ||
                              state.popularFunctions.find(f => f.id === functionId) ||
                              state.recentFunctions.find(f => f.id === functionId);
        }

        if (purchasedFunction) {
          state.purchasedFunctions.push(purchasedFunction);
        }
      });
  }
});

export const {
  setViewMode,
  setFilters,
  clearFilters,
  clearSearchResults,
  clearSelectedFunction,
  updateFunctionRating
} = marketplaceSlice.actions;

export default marketplaceSlice.reducer;